/**
 * The independent check in the Studio (shelf-board-assembly plan Phase 3). The service rebuilds a
 * saved revision and checks it (server/assembly-check.ts); a pack is exported only after a passed
 * check of that revision's current assembly. The state shown in Pack is derived, never stored:
 * a held repair comes first, then a receipt for the current revision and assembly, else unchecked.
 */
import { ASSEMBLY_CHECKER_VERSION, type CheckReceipt } from '@/domain';
import { contractOf } from '@/families/contract';
import { api, ApiError } from './api';
import { toast } from './announce';
import { snap, ui } from './state';

export type CheckState =
  | { kind: 'held' }
  | { kind: 'checking'; revisionId: string }
  | { kind: 'passed' | 'failed'; receipt: CheckReceipt }
  | { kind: 'unchecked' };

/** The receipt for a revision's current assembly from the checker that counts, if any. */
export function receiptFor(revisionId: string, assemblyHash: string): CheckReceipt | null {
  return ui.checks.filter((c) => c.revisionId === revisionId && c.assemblyHash === assemblyHash && c.checkerVersion === ASSEMBLY_CHECKER_VERSION).at(-1) ?? null;
}

export function checkState(): CheckState {
  const s = snap.value;
  if (s.fields.repairs && s.fields.repairs.status === 'unresolved') return { kind: 'held' };
  const rid = s.isDirty || !s.currentRevision ? null : s.currentRevisionId;
  if (!rid) return { kind: 'unchecked' };
  if (ui.checking === rid) return { kind: 'checking', revisionId: rid };
  const r = receiptFor(rid, contractOf(s.build).assemblyHash);
  return r ? { kind: r.status, receipt: r } : { kind: 'unchecked' };
}

let token = 0;

/**
 * Check a saved revision on the service; the receipt replaces any earlier one for the same assembly.
 * A result that comes back after another project was opened is discarded, and only the request that
 * set `checking` clears it.
 */
export async function runCheck(projectId: string, revisionId: string): Promise<CheckReceipt | null> {
  const mine = ++token;
  ui.checking = revisionId;
  try {
    const r = await api.checkRevision(projectId, revisionId);
    if (ui.projectId !== projectId) return null;
    ui.checks = [...ui.checks.filter((c) => !(c.revisionId === r.revisionId && c.assemblyHash === r.assemblyHash && c.checkerVersion === r.checkerVersion)), r];
    return r;
  } catch (e) {
    if (ui.projectId === projectId) toast(`The independent check could not run: ${e instanceof ApiError ? e.message : 'no answer from the service'}`, { tone: 'warn' });
    return null;
  } finally {
    if (token === mine && ui.projectId === projectId) ui.checking = null;
  }
}
